/**
 * Bioperad coherence diagram: one carrier, an operadic composition γ
 * (the multi-input multiplication) and a cooperadic decomposition Δ tied
 * together by a bioperad coherence law. The operadic analogue of the
 * bialgebra coherence square.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useId } from "react";
import { FONT, GRAY, withAlpha } from "../../../design-system/tokens.js";

export function BioperadCoherence({ accent }: { readonly accent: string }): React.JSX.Element {
  const markerId = useId();
  return (
    <figure style={{ margin: 0 }}>
      <figcaption
        style={{
          fontFamily: FONT.body,
          fontSize: "0.8125rem",
          color: GRAY[900],
          marginBottom: "0.25rem",
          fontWeight: 600,
        }}
      >
        Bioperad: γ and Δ on one carrier
      </figcaption>
      <svg
        viewBox="0 0 280 180"
        role="img"
        aria-label="Bioperad coherence: operadic γ and cooperadic Δ on a single carrier H"
      >
        <defs>
          <marker
            id={`${markerId}-bp-head`}
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M0 0 L10 5 L0 10z" fill={accent} />
          </marker>
        </defs>
        <rect
          x={100}
          y={70}
          width={80}
          height={40}
          rx={8}
          fill={withAlpha(accent, "1A")}
          stroke={accent}
          strokeWidth={1.5}
        />
        <text
          x={140}
          y={94}
          textAnchor="middle"
          fontSize="13"
          fontFamily={FONT.mono}
          fill={GRAY[900]}
          fontWeight={700}
        >
          H
        </text>
        <path
          d="M48 90 L100 90"
          stroke={accent}
          strokeWidth={2}
          fill="none"
          markerEnd={`url(#${markerId}-bp-head)`}
        />
        <text
          x={74}
          y={82}
          textAnchor="middle"
          fontSize="11"
          fontFamily={FONT.mono}
          fill={GRAY[700]}
        >
          γ
        </text>
        <text
          x={74}
          y={106}
          textAnchor="middle"
          fontSize="9"
          fontFamily={FONT.body}
          fill={GRAY[600]}
        >
          (compose)
        </text>
        <text
          x={28}
          y={94}
          textAnchor="middle"
          fontSize="11"
          fontFamily={FONT.mono}
          fill={GRAY[700]}
        >
          H⊗ⁿ
        </text>
        <path
          d="M180 90 L232 90"
          stroke={accent}
          strokeWidth={2}
          fill="none"
          markerEnd={`url(#${markerId}-bp-head)`}
        />
        <text
          x={206}
          y={82}
          textAnchor="middle"
          fontSize="11"
          fontFamily={FONT.mono}
          fill={GRAY[700]}
        >
          Δ
        </text>
        <text
          x={206}
          y={106}
          textAnchor="middle"
          fontSize="9"
          fontFamily={FONT.body}
          fill={GRAY[600]}
        >
          (decompose)
        </text>
        <text
          x={252}
          y={94}
          textAnchor="middle"
          fontSize="11"
          fontFamily={FONT.mono}
          fill={GRAY[700]}
        >
          H⊗ᵏ
        </text>
        <path
          d="M48 140 L232 140"
          stroke={GRAY[400]}
          strokeWidth={1.5}
          strokeDasharray="5 4"
          fill="none"
        />
        <text
          x={140}
          y={134}
          textAnchor="middle"
          fontSize="10"
          fontFamily={FONT.mono}
          fill={GRAY[700]}
        >
          Δ ∘ γ ≡ (γ ⊗ γ) ∘ shuffle ∘ (Δ ⊗ Δ)
        </text>
        <text
          x={140}
          y={158}
          textAnchor="middle"
          fontSize="9"
          fontFamily={FONT.body}
          fill={GRAY[600]}
        >
          bioperad coherence — compose-then-decompose factors through decompose-then-compose
        </text>
      </svg>
    </figure>
  );
}
